import type { Season } from "./clock.ts";
import type { GrowthState } from "./growth.ts";
import {
  allTowns,
  realmFoodProduction,
  realmWoodProduction,
  resourceFlagYields,
  settlementPop,
} from "./growth.ts";
import type { HexMap } from "./map.ts";

/**
 * Military still drawing hearth food (optional — economy must not import call.ts).
 * Mouths = realm settlementPop + these spears.
 */
export interface FoodMouthsArms {
  garrison?: number;
  host?: number;
  bannersGarrisoned?: number;
  bannersInTransit?: number;
}

/**
 * Food mouths for season sinks / hunger: settlement souls + garrisoned + host
 * (+ banner garrison / in-transit still on the realm stores).
 */
export function foodMouths(
  growth: GrowthState | undefined,
  arms?: FoodMouthsArms | null,
): number {
  if (!growth) return 0;
  let settle = 0;
  for (const t of allTowns(growth)) {
    settle += settlementPop(t);
  }
  const mil =
    Math.max(0, arms?.garrison ?? 0) +
    Math.max(0, arms?.host ?? 0) +
    Math.max(0, arms?.bannersGarrisoned ?? 0) +
    Math.max(0, arms?.bannersInTransit ?? 0);
  return settle + mil;
}

/** Day-one goods. Luxury stays locked until a later phase. */
export type ResourceId = "food" | "gold" | "iron" | "wood" | "horses";

export const RESOURCE_IDS: readonly ResourceId[] = [
  "food",
  "gold",
  "iron",
  "wood",
  "horses",
] as const;

export const RESOURCE_LABELS: Record<ResourceId, string> = {
  food: "Food",
  gold: "Gold",
  iron: "Iron",
  wood: "Wood",
  horses: "Horses",
};

export type TradeStance = "open" | "cautious" | "closed";

export interface BannermanCard {
  key: string;
  name: string;
  /** Thin yield hint (not a full stock). */
  yieldHint: string;
  loyalty: number; // 0–100
  tradeStance: TradeStance;
  /** One instruct improvement slot (P2 stub). */
  instruct: string;
}

export interface SeatPanel {
  name: string;
  title: string;
  liegeHint: string;
  garrisonHint: string;
  note: string;
}

export type StockMap = Record<ResourceId, number>;

export interface LedgerState {
  stocks: StockMap;
  /** Luxury locked late — UI hides or shows locked gate only. */
  luxuryUnlocked: false;
  seat: SeatPanel;
  bannermen: BannermanCard[];
  /** Last tick lines for HUD (production / sinks). */
  lastTickLog: string[];
  /** True after at least one winter food-prod lock was applied. */
  sawWinterFoodLock: boolean;
}

export interface TickLine {
  resource: ResourceId | "note";
  delta: number;
  reason: string;
}

/** Starting stocks — lean enough that winter greed hurts in ~10 turns. */
export function defaultStocks(): StockMap {
  return {
    food: 28,
    gold: 40,
    iron: 12,
    wood: 18,
    horses: 6,
  };
}

export function createSeat(name = "Your Seat"): SeatPanel {
  return {
    name,
    title: "Knight of the realm",
    liegeHint: "Oath to your liege (CtA Phase 3)",
    garrisonHint: "Standing arms at seat (muster later)",
    note: "Capital only — bannermen are thin cards, not painted counties.",
  };
}

/** Seed thin vassal cards from map keys/names. */
export function createBannermen(
  entries: { key: string; name: string }[],
): BannermanCard[] {
  const stances: TradeStance[] = ["open", "cautious", "closed"];
  const yields = [
    "Grain tithe",
    "Timber levy",
    "Horse pastures",
    "Iron diggings",
    "River tolls",
  ];
  const instructs = [
    "Improve granary",
    "Clear timber road",
    "Expand stables",
    "Shore up diggings",
    "Court the reeve",
  ];
  return entries.map((e, i) => ({
    key: e.key,
    name: e.name,
    yieldHint: yields[i % yields.length]!,
    loyalty: 55 + ((i * 13) % 30),
    tradeStance: stances[i % stances.length]!,
    instruct: instructs[i % instructs.length]!,
  }));
}

export function createLedger(
  bannermen: { key: string; name: string }[] = [],
  seatName = "Your Seat",
): LedgerState {
  return {
    stocks: defaultStocks(),
    luxuryUnlocked: false,
    seat: createSeat(seatName),
    bannermen: createBannermen(bannermen),
    lastTickLog: ["Stores ready — stockpile before Winter."],
    sawWinterFoodLock: false,
  };
}

/**
 * Seasonal production before sinks.
 * Winter food production is hard-locked at 0 (plan P2).
 * Other magnitudes are open/tunable stubs.
 */
export function seasonProduction(season: Season): StockMap {
  switch (season) {
    case "Spring":
      return { food: 6, gold: 3, iron: 1, wood: 3, horses: 1 };
    case "Summer":
      return { food: 4, gold: 5, iron: 2, wood: 2, horses: 1 };
    case "Autumn":
      return { food: 14, gold: 4, iron: 1, wood: 4, horses: 1 };
    case "Winter":
      // Food production = 0 (hard lock). Others reduced/steady.
      return { food: 0, gold: 2, iron: 1, wood: 1, horses: 0 };
  }
}

/** Sum strategic deposit bonuses across seat + banner claims. */
export function realmResourceFlagYields(
  map: HexMap,
  growth: GrowthState,
): { arms: number; gold: number; horses: number; labels: string[] } {
  // Unique claim keys across seat + banners — one deposit pays once
  const keys = new Set<string>();
  for (const town of allTowns(growth)) {
    for (const k of town.claimedKeys) keys.add(k);
  }
  return resourceFlagYields(map, [...keys]);
}

/**
 * Season production with claimed-tile + pop food + deposit flags (3.5).
 * Winter food remains hard-locked at 0; iron/gold/horses still flow from deposits.
 */
export function seasonProductionWithClaims(
  season: Season,
  map?: HexMap,
  growth?: GrowthState,
): StockMap {
  const base = seasonProduction(season);
  if (!map || !growth) return base;
  const flags = realmResourceFlagYields(map, growth);
  const claimFood = season === "Winter" ? 0 : realmFoodProduction(map, growth);
  const claimWood = realmWoodProduction(map, growth);
  return {
    ...base,
    // Winter hard-lock on food prod; deposits still pay iron/gold/horses
    food: season === "Winter" ? 0 : clampStock(base.food + claimFood),
    gold: clampStock(base.gold + flags.gold),
    iron: clampStock(base.iron + flags.arms),
    wood: clampStock(base.wood + claimWood),
    horses: clampStock(base.horses + flags.horses),
  };
}

/** Thin sinks ∝ population (hearth mouths scale gently; gold drain a bit firmer). */
export function seasonSinksWithPop(season: Season, totalPop = 0): StockMap {
  const base = seasonSinks(season);
  // Soft food inflation: ~1 per 32 mouths (was /25 → /30)
  const extraFood = Math.floor(Math.max(0, totalPop) / 32);
  // Firmer gold court drain: ~1 per 28 mouths (was /50 → /35)
  const extraGold = Math.floor(Math.max(0, totalPop) / 28);
  return {
    ...base,
    food: base.food + extraFood,
    gold: base.gold + extraGold,
  };
}


/**
 * Thin job sinks: food upkeep, gold pay, iron arms, wood works, horses muster.
 * Food base eased slightly; gold court drain nudged up so stocks don't pile forever.
 */
export function seasonSinks(season: Season): StockMap {
  const base: StockMap = {
    food: 6, // hearth upkeep (eased — less runaway hunger vs claim food)
    gold: 6, // court / pay (firmer drain so gold doesn't pile forever)
    iron: 1, // arms stub
    wood: 2, // works stub
    horses: 1, // muster/remount stub
  };
  if (season === "Winter") {
    // Hearth tax: food still bites; gold court stays hungry; wood for warmth
    base.food = 9;
    base.gold = 7;
    base.wood = 4; // works 2 + warmth 2
    base.horses = 0;
  } else if (season === "Autumn") {
    base.food = 5; // harvest covers more mouths
  } else if (season === "Summer") {
    base.gold = 7; // campaign pay tease
    base.horses = 2;
  }
  return base;
}

export function clampStock(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n));
}

export function formatDelta(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `${n}`;
  return "0";
}

/** UI-facing signed net: "+4" / "−3" (U+2212) / "0". */
export function formatNetDelta(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return "0";
}

/** Per-resource season net (production − sinks) for HUD / seat panel. */
export function seasonNets(season: Season): StockMap {
  const prod = seasonProduction(season);
  const sinks = seasonSinks(season);
  const out = {} as StockMap;
  for (const id of RESOURCE_IDS) {
    out[id] = prod[id] - sinks[id];
  }
  return out;
}

/** Season nets with claim food + pop-scaled sinks (3.5 HUD). */
export function seasonNetsWithGrowth(
  season: Season,
  map?: HexMap,
  growth?: GrowthState,
  arms?: FoodMouthsArms | null,
): StockMap {
  const mouths = foodMouths(growth, arms);
  const prod = seasonProductionWithClaims(season, map, growth);
  const sinks = seasonSinksWithPop(season, mouths);
  const out = {} as StockMap;
  for (const id of RESOURCE_IDS) {
    out[id] = prod[id] - sinks[id];
  }
  return out;
}

/** HUD nets including claim food + pop sinks when growth is live. */
export function seasonNetsWithClaims(
  season: Season,
  map?: HexMap,
  growth?: GrowthState,
  arms?: FoodMouthsArms | null,
): StockMap {
  const mouths = foodMouths(growth, arms);
  const prod = seasonProductionWithClaims(season, map, growth);
  const sinks = seasonSinksWithPop(season, mouths);
  const out = {} as StockMap;
  for (const id of RESOURCE_IDS) {
    out[id] = prod[id] - sinks[id];
  }
  return out;
}

/**
 * Apply one season tick while the clock is *in* `season` (the season just lived).
 * Call on End turn *before* advancing the clock, using the current season.
 */

/**
 * P3.7 End-of-spring spoilage: 50–75% of food that was in store *before* this
 * season's net is applied is lost (winter leftovers rotting as weather warms).
 * Deterministic mid = 62% when no rng passed; tests may inject rng.
 */
export function springFoodSpoilageAmount(
  foodBeforeTick: number,
  rng: () => number = () => 0.5,
): number {
  if (foodBeforeTick <= 0) return 0;
  // 50–75% inclusive-ish: 0.50 + rng * 0.25
  const rate = 0.5 + Math.min(1, Math.max(0, rng())) * 0.25;
  return clampStock(Math.floor(foodBeforeTick * rate));
}

export function applySeasonTick(
  ledger: LedgerState,
  season: Season,
  map?: HexMap,
  growth?: GrowthState,
  opts: { rng?: () => number; arms?: FoodMouthsArms | null } = {},
): LedgerState {
  const mouths = foodMouths(growth, opts.arms);
  const prod = seasonProductionWithClaims(season, map, growth);
  const sinks = seasonSinksWithPop(season, mouths);
  const next: StockMap = { ...ledger.stocks };
  const lines: string[] = [];
  const winterLock = season === "Winter";
  const claimFood =
    map && growth && !winterLock ? realmFoodProduction(map, growth) : 0;
  const claimWood = map && growth ? realmWoodProduction(map, growth) : 0;
  const flags =
    map && growth ? realmResourceFlagYields(map, growth) : { arms: 0, gold: 0, horses: 0, labels: [] as string[] };

  // Snapshot food before tick for end-of-spring spoilage (winter store rot)
  const foodBefore = ledger.stocks.food;

  for (const id of RESOURCE_IDS) {
    const p = prod[id];
    const s = sinks[id];
    const net = p - s;
    next[id] = clampStock(ledger.stocks[id] + net);
    const bits: string[] = [];
    if (id === "food" && winterLock) {
      bits.push("prod 0 (winter lock)");
    } else if (p !== 0) {
      bits.push(`prod ${formatDelta(p)}`);
    }
    if (id === "food" && claimFood > 0) {
      bits.push(`claims +${claimFood}`);
    }
    if (id === "wood" && claimWood > 0) {
      bits.push(`claims +${claimWood}`);
    }
    if (id === "iron" && flags.arms > 0) bits.push(`deposits +${flags.arms}`);
    if (id === "gold" && flags.gold > 0) bits.push(`deposits +${flags.gold}`);
    if (id === "horses" && flags.horses > 0) bits.push(`deposits +${flags.horses}`);
    if (s !== 0) bits.push(`sink −${s}`);
    bits.push(`net ${formatDelta(net)} → ${next[id]}`);
    lines.push(`${RESOURCE_LABELS[id]}: ${bits.join(" · ")}`);
  }

  // P3.7: End of Spring — spoil 50–75% of food that was in store at season start
  if (season === "Spring") {
    const spoiled = springFoodSpoilageAmount(foodBefore, opts.rng ?? (() => 0.5));
    if (spoiled > 0) {
      next.food = clampStock(next.food - spoiled);
      lines.unshift(
        `Spring thaw spoilage: −${spoiled} food (50–75% of winter stores rotting). Granary now ${next.food}.`,
      );
    }
  }

  if (winterLock) {
    lines.unshift(
      "Winter: food production locked at 0 — eat your stores. Villages burn wood for warmth.",
    );
  } else if (season === "Autumn") {
    lines.unshift("Autumn harvest peaks — fill granaries before the freeze.");
  } else if (claimFood > 0) {
    lines.unshift(`Realm claims yield +${claimFood} food this season.`);
  }
  if (claimWood > 0) {
    lines.unshift(`Claims yield +${claimWood} wood this season.`);
  }
  if (flags.labels.length > 0) {
    lines.unshift(
      `Deposits: +${flags.arms} iron · +${flags.gold} gold · +${flags.horses} horses (${flags.labels.length} claim${flags.labels.length === 1 ? "" : "s"}).`,
    );
  }

  return {
    ...ledger,
    stocks: next,
    lastTickLog: lines,
    sawWinterFoodLock: ledger.sawWinterFoodLock || winterLock,
  };
}

/** Net food change for a season (prod − sink), for tests / HUD. */
export function netFoodForSeason(season: Season): number {
  return seasonProduction(season).food - seasonSinks(season).food;
}
